import { useCallback, useEffect, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import type { ReactNode, RefObject } from 'react'

/** Half the toast's own width (`w-56`), the margin it needs from either edge. */
const HALF_WIDTH_PX = 112
const EDGE_GAP_PX = 8
/** Clear of the button it belongs to, matching the `mb-2` it used to carry. */
const ANCHOR_GAP_PX = 8

/**
 * The "context saved" line, pinned above the control that produced it and
 * drawn outside the footer rather than inside it (BRAI-660).
 *
 * It used to be an `absolute bottom-full` child of the Add Context button, and
 * the footer wraps every control in `overflow-hidden` — that box is what makes
 * the chrome collapse animate, so it cannot simply go. Anything reaching above
 * the control bar was therefore cut off at the bar's top edge, which on screen
 * looks like the Summary panel covering it. A `z-index` never had anything to
 * answer: nothing was painted over it, it was clipped away.
 *
 * Portalled to `document.body` and positioned from the anchor's own rect, so it
 * is outside the clipping box entirely and still lands over its button.
 *
 * @param anchorRef The control to sit above; nothing renders until it has a box.
 * @param children The line to show. Already a live region — the caller's
 *   `role="status"` moves here with it.
 */
export default function ContextSavedToast({
  anchorRef,
  children,
}: {
  anchorRef: RefObject<HTMLElement>
  children: ReactNode
}) {
  const [position, setPosition] = useState<{ left: number; bottom: number } | null>(null)

  const measure = useCallback(() => {
    const anchor = anchorRef.current
    if (!anchor) return
    const rect = anchor.getBoundingClientRect()
    setPosition({
      // Centred on the button, then held off both edges: the bar is centred in
      // a `max-w-2xl` row, but a narrow window can still put this control close
      // enough to the side for a 224px line to hang off it.
      left: Math.min(
        Math.max(rect.left + rect.width / 2, HALF_WIDTH_PX + EDGE_GAP_PX),
        window.innerWidth - HALF_WIDTH_PX - EDGE_GAP_PX
      ),
      bottom: window.innerHeight - rect.top + ANCHOR_GAP_PX,
    })
  }, [anchorRef])

  useLayoutEffect(() => {
    measure()
  }, [measure])

  useEffect(() => {
    // The bar moves when the window resizes and when the mobile chrome folds,
    // and this outlives neither event by much — but five seconds is long
    // enough to rotate a tablet in.
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [measure])

  if (!position) return null

  return createPortal(
    <p
      role="status"
      data-testid="context-saved-toast"
      style={{ left: position.left, bottom: position.bottom }}
      className="fixed z-[200] w-56 -translate-x-1/2 rounded-lg border border-blue-100 bg-white px-3 py-2 text-center text-xs font-medium text-emerald-600 shadow-lg dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)]"
    >
      {children}
    </p>,
    document.body
  )
}
